import React, { useState } from 'react';
import { Group, MantineColor, Popover, Stack, Title } from '@mantine/core';
import { PropsWithChildren } from 'src/PropsWithChildren';
import { resolveWorkflowButtonColor } from 'src/utils';
import { BasicButton } from 'src/design/atoms/BasicButton';

export interface IConfirmPopover extends PropsWithChildren {
  contentTitle: string;
  contentBody: any;
  confirmButtonText: string;
  confirmButtonIntent: MantineColor | string;
  loading?: boolean;
  onConfirmClick: () => Promise<any>;
  interactionKind?: 'click' | 'hover';
  position?: 'right' | 'left' | 'top' | 'bottom';
}

/** @registry Popover wrapping its children as a trigger; shows a confirm/cancel prompt before invoking an async onConfirmClick. */
export const ConfirmPopover: React.FC<IConfirmPopover> = ({
  contentTitle,
  contentBody,
  loading = false,
  confirmButtonIntent,
  confirmButtonText,
  onConfirmClick,
  interactionKind = 'click',
  position = 'right',
  children,
}) => {
  const [opened, setOpened] = useState(false);

  // Convert legacy Blueprint intent to Mantine color if needed
  const buttonColor =
    resolveWorkflowButtonColor(confirmButtonIntent) || confirmButtonIntent;

  const handleConfirm = async () => {
    await onConfirmClick();
    setOpened(false);
  };

  const handleCancel = () => {
    setOpened(false);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position={position}
      withArrow
      shadow="md"
      width={300}
      trapFocus={false}
      closeOnClickOutside
      closeOnEscape
      {...(interactionKind === 'hover' && {
        openDelay: 200,
        closeDelay: 400,
      })}
    >
      <Popover.Target>
        <div
          onClick={() => interactionKind === 'click' && setOpened((o) => !o)}
          onMouseEnter={() => interactionKind === 'hover' && setOpened(true)}
          onMouseLeave={() => interactionKind === 'hover' && setOpened(false)}
          style={{ display: 'inline-block' }}
        >
          {children}
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="md">
          <Title order={5}>{contentTitle}</Title>
          {contentBody}
          <Group justify="flex-end" gap="sm">
            <BasicButton
              variant="light"
              color="gray"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </BasicButton>
            <BasicButton
              color={buttonColor}
              onClick={handleConfirm}
              loading={loading}
            >
              {confirmButtonText}
            </BasicButton>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
